import { useState } from "react";
import QRCode from "react-qr-code";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Wand2, Loader2, Globe, MapPin, Upload, FileText, ExternalLink, Check, Smartphone, Copy, RefreshCw } from "lucide-react";
import PartnerAgreement from "../../components/PartnerAgreement";

const API = process.env.REACT_APP_BACKEND_URL;

const mapsVerifyUrl = (b) => {
  if (b?.latitude && b?.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${b.latitude},${b.longitude}`;
  }
  const q = [b?.name, b?.address].filter(Boolean).join(", ");
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

import { OpenLocationCode } from "open-location-code";

const olc = new OpenLocationCode();

// Parse lat/lng from common Google Maps URL forms or a plus code:
//   https://www.google.com/maps/place/.../@36.5103,-4.8862,17z/...
//   https://www.google.com/maps/@36.5103,-4.8862,17z
//   https://www.google.com/maps/search/?api=1&query=36.5103,-4.8862
//   https://maps.google.com/?q=36.5103,-4.8862
//   "36.5103, -4.8862"  (raw pair)
//   "8FQX5XJX+82"       (full Open Location / plus code)
const parseMapsUrl = (input) => {
  if (!input) return null;
  const s = String(input).trim();
  const at = s.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) return { lat: parseFloat(at[1]), lng: parseFloat(at[2]) };
  const q = s.match(/[?&](?:q|query|destination)=(-?\d+(?:\.\d+)?)[, ]+(-?\d+(?:\.\d+)?)/);
  if (q) return { lat: parseFloat(q[1]), lng: parseFloat(q[2]) };
  const raw = s.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (raw) return { lat: parseFloat(raw[1]), lng: parseFloat(raw[2]) };
  // Plus code support — try to pull a candidate out of the input. Google Maps URLs
  // sometimes embed plus codes in the place segment, e.g. /place/8FQX5XJX+82/...
  const plusCandidate = (s.match(/[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}/i) || [])[0];
  if (plusCandidate) {
    const up = plusCandidate.toUpperCase();
    try {
      if (olc.isFull(up)) {
        const area = olc.decode(up);
        return { lat: area.latitudeCenter, lng: area.longitudeCenter };
      }
    } catch {}
  }
  return null;
};

const BusinessesTab = ({
  businesses,
  subLocations,
  territories = [],
  currentUser,
  onCreateBusiness,
  onEditBusiness,
  onDeleteBusiness,
  onAIExtract,
  onGeocode,
  extracting
}) => {
  const defaultTerritory = currentUser?.territory_id || "costa_del_sol";
  const [showDialog, setShowDialog] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [extractUrl, setExtractUrl] = useState("");
  const [showAgreement, setShowAgreement] = useState(false);
  const [pendingBusiness, setPendingBusiness] = useState(null);
  const [uploadingAgreement, setUploadingAgreement] = useState(null);
  const [mapsUrlInput, setMapsUrlInput] = useState("");
  const [mapsUrlError, setMapsUrlError] = useState("");
  const [scannerLinkBusiness, setScannerLinkBusiness] = useState(null);
  const [scannerLinkLoading, setScannerLinkLoading] = useState(false);
  const [scannerToken, setScannerToken] = useState(null);
  const [scannerCopied, setScannerCopied] = useState(false);

  const openScannerLink = async (b) => {
    setScannerLinkBusiness(b);
    setScannerToken(null);
    setScannerLinkLoading(true);
    setScannerCopied(false);
    try {
      const token = localStorage.getItem("advanta_token");
      const res = await axios.post(`${API}/api/admin/businesses/${b.id}/scanner-token`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScannerToken(res.data.token);
    } catch (err) {
      setScannerToken("");
    } finally {
      setScannerLinkLoading(false);
    }
  };

  const rotateScannerLink = async () => {
    if (!scannerLinkBusiness) return;
    if (!window.confirm("Rotate this venue's scanner link? All currently paired devices will need to re-pair with the new link.")) return;
    setScannerLinkLoading(true);
    setScannerCopied(false);
    try {
      const token = localStorage.getItem("advanta_token");
      const res = await axios.post(`${API}/api/admin/businesses/${scannerLinkBusiness.id}/rotate-scanner-token`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScannerToken(res.data.token);
    } finally {
      setScannerLinkLoading(false);
    }
  };

  const scannerPairUrl = scannerToken ? `${window.location.origin}/scan/pair/${scannerToken}` : "";

  const copyScannerUrl = async () => {
    if (!scannerPairUrl) return;
    try {
      await navigator.clipboard.writeText(scannerPairUrl);
      setScannerCopied(true);
      setTimeout(() => setScannerCopied(false), 2000);
    } catch {}
  };

  const handleUploadAgreement = async (businessId, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Please upload a PDF or image file (JPG, PNG, WebP)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Max 10MB.');
      return;
    }
    setUploadingAgreement(businessId);
    try {
      const token = localStorage.getItem('advanta_token');
      // If it's an image, we upload as-is and let the backend handle it
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/businesses/${businessId}/agreement`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        window.location.reload();
      } else {
        const data = await res.json();
        alert(data.detail || 'Upload failed');
      }
    } catch {
      alert('Upload failed');
    } finally {
      setUploadingAgreement(null);
    }
  };
  const [newBusiness, setNewBusiness] = useState({
    name: "", description: "", address: "", category: "dining", discount_level: 15,
    image_url: "", territory_id: defaultTerritory, website: "", tour_3d_url: "", sub_location: "",
    latitude: null, longitude: null, phone: "", email: "", contact_name: "", contact_position: ""
  });

  const handleClose = () => {
    setShowDialog(false);
    setEditingBusiness(null);
    setExtractUrl("");
    setNewBusiness({
      name: "", description: "", address: "", category: "dining", discount_level: 15,
      image_url: "", territory_id: defaultTerritory, website: "", tour_3d_url: "", sub_location: "",
      latitude: null, longitude: null, phone: "", email: "", contact_name: "", contact_position: ""
    });
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setNewBusiness({
      name: business.name || "",
      description: business.description || "",
      address: business.address || "",
      category: business.category || "dining",
      discount_level: business.discount_level || 15,
      image_url: business.image_url || "",
      territory_id: business.territory_id || "costa_del_sol",
      website: business.website || "",
      tour_3d_url: business.tour_3d_url || "",
      sub_location: business.sub_location || "",
      latitude: business.latitude || null,
      longitude: business.longitude || null,
      phone: business.phone || "",
      email: business.email || "",
      contact_name: business.contact_name || "",
      contact_position: business.contact_position || ""
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (editingBusiness) {
      // Editing — no agreement needed
      await onCreateBusiness(newBusiness, editingBusiness);
      handleClose();
    } else {
      // Validate contact details before agreement
      if (!newBusiness.contact_name || !newBusiness.contact_name.trim()) {
        alert("Please enter a contact name — this person will sign the agreement.");
        return;
      }
      if (!newBusiness.email || !newBusiness.email.trim()) {
        alert("Please enter a contact email — a copy of the agreement will be sent to them.");
        return;
      }
      // New business — show agreement first
      setPendingBusiness(newBusiness);
      setShowDialog(false);
      setShowAgreement(true);
    }
  };

  const handleSignAgreement = async (signedPdfBlob) => {
    setShowAgreement(false);
    if (pendingBusiness) {
      await onCreateBusiness(pendingBusiness, null, signedPdfBlob);
      setPendingBusiness(null);
      handleClose();
    }
  };

  const handleAIExtractClick = async () => {
    const data = await onAIExtract(extractUrl);
    if (data) {
      setNewBusiness(prev => ({
        ...prev,
        name: data.name || prev.name,
        description: data.description || prev.description,
        address: data.address || prev.address,
        category: data.category || prev.category,
        website: data.website || extractUrl,
        latitude: data.latitude || prev.latitude,
        longitude: data.longitude || prev.longitude,
        image_url: data.image_url || prev.image_url,
        phone: data.phone || prev.phone,
        email: data.email || prev.email,
        contact_name: data.contact_name || prev.contact_name
      }));
    }
  };

  const handleGeocodeClick = async () => {
    const result = await onGeocode(newBusiness.address);
    if (result) {
      setNewBusiness(prev => ({
        ...prev,
        latitude: result.latitude,
        longitude: result.longitude
      }));
    }
  };

  return (
    <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
      <div className="p-4 border-b border-[#D4AF37]/10 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-['Playfair_Display'] text-xl text-white">Partner Businesses</h3>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/api/contract/download/en`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-white/70 hover:text-white transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Contract (EN)
          </a>
          <a
            href={`${API}/api/contract/download/es`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-white/70 hover:text-white transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Contrato (ES)
          </a>
        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) handleClose(); else setShowDialog(true); }}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-business-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Business
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl">
                {editingBusiness ? 'Edit Business' : 'Add New Business'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* AI Extraction Section */}
              {!editingBusiness && (
                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-4 border border-purple-500/20">
                  <Label className="text-purple-400 flex items-center gap-2 mb-2">
                    <Wand2 className="w-4 h-4" />
                    AI Auto-Fill from Website
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={extractUrl}
                      onChange={(e) => setExtractUrl(e.target.value)}
                      className="bg-white/5 border-white/10 text-white flex-1"
                      placeholder="https://business-website.com"
                      data-testid="ai-extract-url"
                    />
                    <Button
                      onClick={handleAIExtractClick}
                      disabled={extracting}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                      data-testid="ai-extract-btn"
                    >
                      {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-white/40 mt-2">Paste a website URL to auto-fill business details</p>
                </div>
              )}

              {/* Contact Details */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <Label className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3 block">Contact / Representative</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Contact Name *</Label>
                    <Input
                      value={newBusiness.contact_name}
                      onChange={(e) => setNewBusiness({ ...newBusiness, contact_name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Position in Company</Label>
                    <Input
                      value={newBusiness.contact_position}
                      onChange={(e) => setNewBusiness({ ...newBusiness, contact_position: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Owner / Manager / Director"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Contact Email *</Label>
                    <Input
                      type="email"
                      value={newBusiness.email}
                      onChange={(e) => setNewBusiness({ ...newBusiness, email: e.target.value })}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="john@business.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-white/70">Business Name *</Label>
                <Input
                  value={newBusiness.name}
                  onChange={(e) => setNewBusiness({ ...newBusiness, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="business-name-input"
                  placeholder="Restaurant El Sol"
                />
              </div>
              <div>
                <Label className="text-white/70">Description *</Label>
                <textarea
                  value={newBusiness.description}
                  onChange={(e) => setNewBusiness({ ...newBusiness, description: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white mt-1 rounded-md p-3 min-h-[80px]"
                  placeholder="Tell us about this business..."
                />
              </div>
              <div>
                <Label className="text-white/70">Address *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newBusiness.address}
                    onChange={(e) => setNewBusiness({ ...newBusiness, address: e.target.value })}
                    className="bg-white/5 border-white/10 text-white flex-1"
                    placeholder="Calle Principal 123, Marbella"
                  />
                  <Button
                    onClick={handleGeocodeClick}
                    variant="outline"
                    className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    title="Auto-find coordinates from address"
                  >
                    <Globe className="w-4 h-4" />
                  </Button>
                </div>

                {/* Coordinate status + in-tab map preview */}
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                  {newBusiness.latitude && newBusiness.longitude ? (
                    <>
                      <span className="text-green-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Pin set: {newBusiness.latitude.toFixed(5)}, {newBusiness.longitude.toFixed(5)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNewBusiness({ ...newBusiness, latitude: null, longitude: null })}
                        className="text-white/40 hover:text-white/70 transition-colors"
                      >
                        clear pin
                      </button>
                    </>
                  ) : newBusiness.address ? (
                    <span className="text-white/40">No pin — map below is a name/address search. Click the globe button to drop a pin.</span>
                  ) : null}
                </div>
                {(newBusiness.latitude && newBusiness.longitude) || newBusiness.address ? (
                  <div className="mt-2 rounded-lg overflow-hidden border border-white/10">
                    <iframe
                      title="Business location preview"
                      src={
                        newBusiness.latitude && newBusiness.longitude
                          ? `https://maps.google.com/maps?q=${newBusiness.latitude},${newBusiness.longitude}&z=17&output=embed`
                          : `https://maps.google.com/maps?q=${encodeURIComponent([newBusiness.name, newBusiness.address].filter(Boolean).join(", "))}&z=16&output=embed`
                      }
                      width="100%"
                      height="180"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      data-testid="business-map-preview"
                    />
                  </div>
                ) : null}

                {/* Paste a Google Maps URL, lat/lng, or plus code to lock in exact coordinates */}
                <div className="mt-2">
                  <Label className="text-white/40 text-xs">Or paste a Google Maps link, coordinates, or plus code</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={mapsUrlInput}
                      onChange={(e) => { setMapsUrlInput(e.target.value); setMapsUrlError(""); }}
                      className="bg-white/5 border-white/10 text-white flex-1 text-xs"
                      placeholder="Maps URL  |  36.5103,-4.8862  |  8FQX5XJX+82 (plus code)"
                      data-testid="maps-url-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const parsed = parseMapsUrl(mapsUrlInput);
                        if (!parsed) {
                          setMapsUrlError("Couldn't read coordinates — paste a Maps URL, 'lat,lng', or a full plus code (e.g. 8FQX5XJX+82)");
                          return;
                        }
                        setNewBusiness({ ...newBusiness, latitude: parsed.lat, longitude: parsed.lng });
                        setMapsUrlInput("");
                        setMapsUrlError("");
                      }}
                      className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs"
                    >
                      Set
                    </Button>
                  </div>
                  {mapsUrlError && <p className="text-red-400 text-xs mt-1">{mapsUrlError}</p>}
                  <p className="text-white/30 text-[10px] mt-1">
                    Tip: drop a pin on Google Maps → tap the place card → the plus code or coordinates appear → Copy → paste here. Plus codes look like <span className="font-mono">8FQX5XJX+82</span>.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Location Area</Label>
                  <Select value={newBusiness.sub_location || "none"} onValueChange={(v) => setNewBusiness({ ...newBusiness, sub_location: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific area</SelectItem>
                      {subLocations.filter(loc => !loc.territory_id || loc.territory_id === newBusiness.territory_id).map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70">Territory *</Label>
                  <Select value={newBusiness.territory_id} onValueChange={(v) => setNewBusiness({ ...newBusiness, territory_id: v, sub_location: "" })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {territories.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70">Category *</Label>
                  <Select value={newBusiness.category} onValueChange={(v) => setNewBusiness({ ...newBusiness, category: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dining">Dining & Restaurants</SelectItem>
                      <SelectItem value="bar">Bars & Nightlife</SelectItem>
                      <SelectItem value="shopping">Shopping & Retail</SelectItem>
                      <SelectItem value="services">Services & Experiences</SelectItem>
                      <SelectItem value="beauty">Beauty & Wellness</SelectItem>
                      <SelectItem value="activities">Activities & Excursions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Discount % *</Label>
                  <Input
                    type="number"
                    min="10"
                    max="20"
                    value={newBusiness.discount_level}
                    onChange={(e) => setNewBusiness({ ...newBusiness, discount_level: parseInt(e.target.value) })}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Phone Number</Label>
                  <Input
                    value={newBusiness.phone}
                    onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    placeholder="+34 612 345 678"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Website (display only, not linked)</Label>
                <Input
                  value={newBusiness.website}
                  onChange={(e) => setNewBusiness({ ...newBusiness, website: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="www.example.com"
                />
              </div>
              <div>
                <Label className="text-white/70">3D Virtual Tour URL</Label>
                <Input
                  value={newBusiness.tour_3d_url}
                  onChange={(e) => setNewBusiness({ ...newBusiness, tour_3d_url: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="https://my.matterport.com/..."
                />
              </div>
              <div>
                <Label className="text-white/70">Business Image</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newBusiness.image_url}
                    onChange={(e) => setNewBusiness({ ...newBusiness, image_url: e.target.value })}
                    className="bg-white/5 border-[#D4AF37]/30 text-white flex-1 placeholder:text-white/40"
                    placeholder="Paste image URL here"
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        if (text && (text.startsWith('http://') || text.startsWith('https://'))) {
                          setNewBusiness(prev => ({ ...prev, image_url: text.trim() }));
                        } else {
                          alert('No valid URL found in clipboard. Copy an image URL first.');
                        }
                      } catch {
                        alert('Could not read clipboard. Please paste the URL manually.');
                      }
                    }}
                    variant="outline"
                    className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 whitespace-nowrap"
                    title="Paste image URL from clipboard"
                  >
                    Paste URL
                  </Button>
                  <label className="flex items-center gap-1.5 px-3 py-2 bg-[#D4AF37] hover:bg-[#c4a030] text-black rounded-md cursor-pointer text-sm font-medium whitespace-nowrap">
                    <Upload className="w-4 h-4" />
                    Upload
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { alert('Image too large. Max 5MB.'); return; }
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const token = localStorage.getItem('advanta_token');
                        const res = await fetch(`${API}/upload`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        const data = await res.json();
                        if (data.url) setNewBusiness(prev => ({ ...prev, image_url: data.url }));
                        else alert('Upload failed');
                      } catch { alert('Upload failed'); }
                      e.target.value = '';
                    }} />
                  </label>
                </div>
                {newBusiness.image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-white/10">
                    <img
                      src={newBusiness.image_url.startsWith('/') ? `${API}${newBusiness.image_url}` : newBusiness.image_url}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-business-btn">
                {editingBusiness ? 'Update Business' : 'Continue to Agreement'}
              </Button>
              {!editingBusiness && (
                <Button
                  variant="outline"
                  onClick={async () => { await onCreateBusiness(newBusiness, null); handleClose(); }}
                  className="w-full border-white/20 text-white/60 hover:text-white mt-2"
                >
                  Save as Draft (sign later)
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="businesses-table">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Name</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Category</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Discount</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Status</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Agreement</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Territory</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {businesses.map((b) => (
              <tr key={b.id} className="border-t border-white/5">
                <td className="p-4 text-white">{b.name}</td>
                <td className="p-4 text-white/70 capitalize">{b.category}</td>
                <td className="p-4 text-[#047857] font-bold">{b.discount_level}%</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${b.status === 'active' ? 'bg-green-500/20 text-green-400' : b.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/50'}`}>
                    {b.status || 'active'}
                  </span>
                  {b.status === 'draft' && (
                    <button
                      onClick={async (e) => { e.stopPropagation(); try { await onEditBusiness({...b, status: 'active'}, b); } catch (err) { console.error('Activate failed', err); } }}
                      className="ml-2 text-xs text-green-400 hover:text-green-300 underline"
                    >
                      Activate
                    </button>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    {b.signed_agreement_url ? (
                      <a
                        href={`${API}${b.signed_agreement_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-[#D4AF37] hover:text-[#D4AF37]/80 text-xs"
                      >
                        <FileText className="w-4 h-4" />
                        View PDF
                      </a>
                    ) : (
                      <span className="text-white/30 text-xs">Not signed</span>
                    )}
                    <label className="flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs text-white/60 hover:text-white cursor-pointer transition-colors">
                      {uploadingAgreement === b.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Upload className="w-3 h-3" />
                      )}
                      {b.signed_agreement_url ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        disabled={uploadingAgreement === b.id}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadAgreement(b.id, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </td>
                <td className="p-4 text-white/50 capitalize">{b.territory_id}</td>
                <td className="p-4 flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openScannerLink(b)}
                    className="text-[#D4AF37] hover:text-[#D4AF37]/80"
                    title="Scanner pairing link for this venue"
                    data-testid={`scanner-link-${b.id}`}
                  >
                    <Smartphone className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(b)}
                    className="text-[#D4AF37] hover:text-[#D4AF37]/80"
                    data-testid={`edit-business-${b.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteBusiness(b.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Scanner pairing link modal */}
      <Dialog open={!!scannerLinkBusiness} onOpenChange={(open) => { if (!open) { setScannerLinkBusiness(null); setScannerToken(null); } }}>
        <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#D4AF37]" />
              Scanner pairing link
            </DialogTitle>
          </DialogHeader>
          {scannerLinkBusiness && (
            <div className="space-y-4 mt-2">
              <p className="text-white/60 text-sm">
                Open this link <span className="text-white">once on every staff phone</span> that will scan customer QR codes at <span className="text-white font-medium">{scannerLinkBusiness.name}</span>. Each device stays linked until it's unpaired or the link is rotated.
              </p>

              {scannerLinkLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                </div>
              )}

              {!scannerLinkLoading && scannerToken && (
                <>
                  <div className="bg-white rounded-xl p-4 flex items-center justify-center">
                    <QRCode value={scannerPairUrl} size={180} />
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Pairing URL</p>
                    <p className="text-[#D4AF37] text-xs break-all font-mono">{scannerPairUrl}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={copyScannerUrl}
                      className="btn-gold flex-1"
                      data-testid="copy-scanner-url"
                    >
                      {scannerCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {scannerCopied ? "Copied" : "Copy link"}
                    </Button>
                    <Button
                      onClick={rotateScannerLink}
                      variant="outline"
                      className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                      title="Generate a new link and invalidate the current one"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rotate
                    </Button>
                  </div>
                  <p className="text-white/40 text-[10px]">
                    Share the link by message, email or by showing the QR. The first time a phone opens it, that device is locked to this venue — every scan from then on attributes to {scannerLinkBusiness.name} in analytics.
                  </p>
                </>
              )}

              {!scannerLinkLoading && scannerToken === "" && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                  Couldn't generate a pairing link — please try again.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partner Agreement Modal */}
      {showAgreement && pendingBusiness && (
        <PartnerAgreement
          businessName={pendingBusiness.name}
          contactName={pendingBusiness.contact_name || currentUser?.name || currentUser?.email || "Admin"}
          contactPosition={pendingBusiness.contact_position || ""}
          onSign={handleSignAgreement}
          onCancel={() => { setShowAgreement(false); setPendingBusiness(null); setShowDialog(true); }}
        />
      )}
    </div>
  );
};

export default BusinessesTab;
